import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../navbar/navbar";
import Background from "../Background/Background";
import "./api-tester.css";
import config from "../../config/config.json";


function ApiTesterPage({ isAIChatOpen, openAIChat,theme, toggleTheme }) {
  /* Server */
  const [serverUrl, setServerUrl] = useState(config.apiTesterURL || "");
  const [serverOnline, setServerOnline] = useState(null);

  /* UI */
  const [activeTab, setActiveTab] = useState("auth");
  const [authHeader, setAuthHeader] = useState("");
  const [response, setResponse] = useState("");
  const [showAuthWarn, setShowAuthWarn] = useState(false);

  /* Auth */
  const [regUser, setRegUser] = useState("");
  const [regPass, setRegPass] = useState("");
  const [regUrl, setRegUrl] = useState("/auth/register");
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginUrl, setLoginUrl] = useState("/auth/login");
  const [token, setToken] = useState("");

  /* CRUD */
  const [crudOp, setCrudOp] = useState("getAll");
  const [createUrl, setCreateUrl] = useState("/items");
  const [createTitle, setCreateTitle] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [getAllUrl, setGetAllUrl] = useState("/items");
  const [fields, setFields] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [limit, setLimit] = useState("");
  const [offset, setOffset] = useState("");
  const [q, setQ] = useState("");
  const [getOneUrl, setGetOneUrl] = useState("/items/");
  const [getId, setGetId] = useState("");
  const [updateUrl, setUpdateUrl] = useState("/items/");
  const [updateId, setUpdateId] = useState("");
  const [updateTitle, setUpdateTitle] = useState("");
  const [updateDesc, setUpdateDesc] = useState("");
  const [deleteUrl, setDeleteUrl] = useState("/items/");
  const [deleteId, setDeleteId] = useState("");

  /* Docs */
  const [expanded, setExpanded] = useState({});

  /* ── Server poll ── */
  const checkServer = useCallback(async () => {
    try { await fetch(serverUrl); setServerOnline(true); }
    catch { setServerOnline(false); }
  }, [serverUrl]);

  useEffect(() => {
    checkServer();
    const id = setInterval(checkServer, 5000);
    return () => clearInterval(id);
  }, [checkServer]);

  const out = (data) => setResponse(JSON.stringify(data, null, 2));

  const clearAuth = () => {
    setAuthHeader(""); setBasicUser(""); setBasicPass(""); setToken("");
    alert("Authorization cleared");
    setShowAuthWarn(true);
  };

  /* ── Auth ── */
  const register = async () => {
    try {
      const res = await fetch(serverUrl + regUrl, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: regUser, password: regPass }),
      });
      out(await res.json());
    } catch (e) { out({ error: e.message }); }
  };

  const login = async () => {
    try {
      const res = await fetch(serverUrl + loginUrl, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: loginUser, password: loginPass }),
      });
      const data = await res.json();
      out(data);
      if (data.token) setToken(data.token);
    } catch (e) { out({ error: e.message }); }
  };

  const useBasic = () => {
    if (!basicUser || !basicPass) { alert("Enter username and password"); return; }
    setAuthHeader("Basic " + btoa(basicUser + ":" + basicPass));
    alert("Basic Auth Activated");
    setShowAuthWarn(false);
  };

  const useBearer = () => {
    if (!token) { alert("Enter token"); return; }
    setAuthHeader("Bearer " + token);
    alert("Bearer Token Activated");
    setShowAuthWarn(false);
  };

  /* ── CRUD ── */
  const createItem = async () => {
    try {
      const res = await fetch(serverUrl + createUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: authHeader },
        body: JSON.stringify({ title: createTitle, description: createDesc }),
      });
      out(await res.json());
    } catch (e) { out({ error: e.message }); }
  };

  const getItems = async () => {
    try {
      let url = serverUrl + getAllUrl;
      const p = new URLSearchParams();
      if (fields) p.append("fields", fields);
      if (orderBy) p.append("orderBy", orderBy);
      if (limit) p.append("limit", limit);
      if (offset) p.append("offset", offset);
      if (q) p.append("q", q);
      if (p.toString()) url += "?" + p.toString();
      const res = await fetch(url, { headers: { Authorization: authHeader } });
      out(await res.json());
    } catch (e) { out({ error: e.message }); }
  };

  const getItem = async () => {
    try {
      const res = await fetch(serverUrl + getOneUrl, { headers: { Authorization: authHeader } });
      out(await res.json());
    } catch (e) { out({ error: e.message }); }
  };

  const updateItem = async () => {
    try {
      const res = await fetch(serverUrl + updateUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: authHeader },
        body: JSON.stringify({ title: updateTitle, description: updateDesc }),
      });
      out(await res.json());
    } catch (e) { out({ error: e.message }); }
  };

  const deleteItem = async () => {
    try {
      const res = await fetch(serverUrl + deleteUrl, {
        method: "DELETE", headers: { Authorization: authHeader },
      });
      out(await res.json());
    } catch (e) { out({ error: e.message }); }
  };

  const toggleDoc = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const authDocs = [
    { key: "register", method: "POST", path: "/auth/register", body: `Request Body:\n{\n  "username": "string",\n  "password": "string"\n}\n\nResponse (201 Created):\n{\n  "message": "User registered successfully"\n}` },
    { key: "login", method: "POST", path: "/auth/login", body: `Request Body:\n{\n  "username": "string",\n  "password": "string"\n}\n\nResponse (200 OK):\n{\n  "message": "Login successful",\n  "type": "Bearer",\n  "token": "JWT_TOKEN_HERE"\n}` },
  ];
  const crudDocs = [
    { key: "createItem", method: "POST", path: "/items", body: `Request Body:\n{\n  "title": "string (required)",\n  "description": "string (optional)"\n}\n\nResponse (201 Created):\n{\n  "message": "Item created successfully",\n  "item": { "id": "uuid", "title": "...", ... }\n}` },
    { key: "getItems", method: "GET", path: "/items", body: `Query Parameters:\n- limit: number (Default 25, Max 100)\n- offset: number (Default 0)\n- fields: string (Comma separated, e.g. "title,id")\n- orderBy: string (Format "attr:direction", e.g. "title:asc")\n- q: string (Format "attr='value'", e.g. "title='test'")\n\nResponse (200 OK):\n{\n  "totalItems": number,\n  "items": [...]\n}` },
    { key: "getItem", method: "GET", path: "/items/{id}", body: `Response (200 OK):\n{\n  "message": "Item fetched successfully",\n  "item": { ... }\n}\n\nError (404 Not Found):\n{ "message": "Item not found" }` },
    { key: "updateItem", method: "PUT", path: "/items/{id}", body: `Request Body:\n{\n  "title": "string",\n  "description": "string"\n}\n\nResponse (200 OK):\n{\n  "message": "Item updated successfully",\n  "item": { ... }\n}` },
    { key: "deleteItem", method: "DELETE", path: "/items/{id}", body: `Response (200 OK):\n{\n  "message": "Item deleted successfully",\n  "item": { ...deleted item object... }\n}` },
  ];

  return (
    <>
      <Background />
      <Navbar
        currentPage="api"
        theme={theme}
        toggleTheme={toggleTheme}
        isAIChatOpen={isAIChatOpen}
        openAIChat={openAIChat}
      />

      <div className="atp-page">
        <div className="atp-header">
          <h3 className="atp-title"></h3>
          <div className="atp-server-box">
            <span>Server URL:</span>
            <input
              className="atp-server-input"
              value={serverUrl}
              onChange={e => setServerUrl(e.target.value)}
            />
            <span
              className="atp-server-status"
              style={{ color: serverOnline === true ? "#4ade80" : serverOnline === false ? "#f87171" : "#fbbf24" }}
            >
              {serverOnline === null ? "⏳ Checking" : serverOnline ? "🟢 Online" : "🔴 Offline"}
            </span>
            <button className="atp-clear-btn" onClick={clearAuth}>Clear Auth</button>
          </div>
        </div>

        <div className="atp-tabs">
          <div className={`atp-tab ${activeTab === "auth" ? "active" : ""}`} onClick={() => setActiveTab("auth")}>Auth</div>
          <div className={`atp-tab ${activeTab === "crud" ? "active" : ""}`} onClick={() => { setActiveTab("crud"); if (!authHeader) setShowAuthWarn(true); }}>CRUD</div>
          <div className={`atp-tab ${activeTab === "doc" ? "active" : ""}`} onClick={() => setActiveTab("doc")}>Documentation</div>
        </div>
        <div className="atp-container">

          {/* AUTH TAB */}
          {activeTab === "auth" && (
            <>
              {/* Register */}
              <div className="atp-section">
                <div className="atp-section-header">
                  <h3>Register</h3>
                  <div className="atp-url-box">
                    <input value={regUrl} onChange={e => setRegUrl(e.target.value)} />
                  </div>
                </div>
                <div className="atp-row">
                  <input value={regUser} onChange={e => setRegUser(e.target.value)} placeholder="username *" />
                  <input value={regPass} onChange={e => setRegPass(e.target.value)} placeholder="password *" />
                  <button onClick={register}>Register</button>
                </div>
              </div>

              {/* Basic Auth */}
              <div className="atp-section">
                <div className="atp-section-header">
                  <h3>Basic Auth</h3>
                  <button className="atp-default-btn" onClick={() => { setBasicUser("admin"); setBasicPass("admin123"); }}>Use Default Credentials</button>
                </div>
                <div className="atp-row">
                  <input value={basicUser} onChange={e => setBasicUser(e.target.value)} placeholder="username *" />
                  <input value={basicPass} onChange={e => setBasicPass(e.target.value)} placeholder="password *" />
                  <button onClick={useBasic}>Use Basic Auth</button>
                </div>
              </div>

              {/* Login → Bearer */}
              <div className="atp-section">
                <div className="atp-section-header">
                  <h3>Login → Bearer Token</h3>
                  <div className="atp-url-box">
                    <input value={loginUrl} onChange={e => setLoginUrl(e.target.value)} />
                  </div>
                  <button className="atp-default-btn" onClick={() => { setLoginUser("admin"); setLoginPass("admin123"); }}>Use Default Credentials</button>
                </div>
                <div className="atp-row">
                  <input value={loginUser} onChange={e => setLoginUser(e.target.value)} placeholder="username *" />
                  <input value={loginPass} onChange={e => setLoginPass(e.target.value)} placeholder="password *" />
                  <button onClick={login}>Login</button>
                </div>
                <div className="atp-row" style={{ marginTop: 5 }}>
                  <input style={{ width: 400 }} value={token} onChange={e => setToken(e.target.value)} placeholder="token" />
                  <button onClick={useBearer}>Use Bearer</button>
                </div>
              </div>
            </>
          )}

          {/* CRUD TAB */}
          {activeTab === "crud" && (
            <>
              {showAuthWarn && (
                <div className="atp-warning">⚠ Authorization not set. Please use Basic Auth or Bearer Token from Auth tab.</div>
              )}

              <div className="atp-section">
                <div className="atp-crud-selector">
                  Select Operation:&nbsp;
                  <select value={crudOp} onChange={e => setCrudOp(e.target.value)}>
                    <option value="getAll">Get Items (All)</option>
                    <option value="getOne">Get Item (By ID)</option>
                    <option value="create">Create Item</option>
                    <option value="update">Update Item</option>
                    <option value="delete">Delete Item</option>
                  </select>
                </div>

                {crudOp === "getAll" && (
                  <div className="atp-crud-panel">
                    <div className="atp-section-header">
                      <h3>Get Items</h3>
                      <div className="atp-url-box"><input value={getAllUrl} onChange={e => setGetAllUrl(e.target.value)} /></div>
                    </div>
                    <div className="atp-row">
                      <input value={fields} onChange={e => setFields(e.target.value)} placeholder="fields (e.g. title,id)" />
                      <input value={orderBy} onChange={e => setOrderBy(e.target.value)} placeholder="orderBy (e.g. title:asc)" />
                      <input value={limit} onChange={e => setLimit(e.target.value)} placeholder="limit (max 100)" />
                      <input value={offset} onChange={e => setOffset(e.target.value)} placeholder="offset" />
                      <input value={q} onChange={e => setQ(e.target.value)} placeholder="q (e.g. title='item')" />
                      <button className="atp-default-btn" onClick={getItems}>Send</button>
                    </div>
                  </div>
                )}

                {crudOp === "getOne" && (
                  <div className="atp-crud-panel">
                    <div className="atp-section-header">
                      <h3>Get Item</h3>
                      <div className="atp-url-box"><input value={getOneUrl} readOnly /></div>
                    </div>
                    <div className="atp-row">
                      <input value={getId} onChange={e => { setGetId(e.target.value); setGetOneUrl("/items/" + e.target.value); }} placeholder="id *" />
                      <button className="atp-default-btn" onClick={getItem}>Send</button>
                    </div>
                  </div>
                )}

                {crudOp === "create" && (
                  <div className="atp-crud-panel">
                    <div className="atp-section-header">
                      <h3>Create Item</h3>
                      <div className="atp-url-box"><input value={createUrl} onChange={e => setCreateUrl(e.target.value)} /></div>
                    </div>
                    <div className="atp-row">
                      <input value={createTitle} onChange={e => setCreateTitle(e.target.value)} placeholder="title *" />
                      <input value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder="description" />
                      <button className="atp-default-btn" onClick={createItem}>Send</button>
                    </div>
                  </div>
                )}

                {crudOp === "update" && (
                  <div className="atp-crud-panel">
                    <div className="atp-section-header">
                      <h3>Update Item</h3>
                      <div className="atp-url-box"><input value={updateUrl} readOnly /></div>
                    </div>
                    <div className="atp-row">
                      <input value={updateId} onChange={e => { setUpdateId(e.target.value); setUpdateUrl("/items/" + e.target.value); }} placeholder="id *" />
                      <input value={updateTitle} onChange={e => setUpdateTitle(e.target.value)} placeholder="title" />
                      <input value={updateDesc} onChange={e => setUpdateDesc(e.target.value)} placeholder="description" />
                      <button className="atp-default-btn" onClick={updateItem}>Send</button>
                    </div>
                  </div>
                )}

                {crudOp === "delete" && (
                  <div className="atp-crud-panel">
                    <div className="atp-section-header">
                      <h3>Delete Item</h3>
                      <div className="atp-url-box"><input value={deleteUrl} readOnly /></div>
                    </div>
                    <div className="atp-row">
                      <input value={deleteId} onChange={e => { setDeleteId(e.target.value); setDeleteUrl("/items/" + e.target.value); }} placeholder="id *" />
                      <button className="atp-clear-btn" onClick={deleteItem}>Send</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* DOC TAB */}
          {activeTab === "doc" && (
            <>
              <h3>Authentication</h3>
              {authDocs.map(d => (
                <div key={d.key} className="atp-doc-block">
                  <div className="atp-doc-header" onClick={() => toggleDoc(d.key)}>
                    <span className={`atp-method ${d.method.toLowerCase()}`}>{d.method}</span> {d.path}
                  </div>
                  {expanded[d.key] && <div className="atp-doc-body"><pre>{d.body}</pre></div>}
                </div>
              ))}
              <h3 style={{ marginTop: 20 }}>Items CRUD</h3>
              <p><small>All Item endpoints require <strong>Authorization</strong> (Basic or Bearer).</small></p>
              {crudDocs.map(d => (
                <div key={d.key} className="atp-doc-block">
                  <div className="atp-doc-header" onClick={() => toggleDoc(d.key)}>
                    <span className={`atp-method ${d.method.toLowerCase()}`}>{d.method}</span> {d.path}
                  </div>
                  {expanded[d.key] && <div className="atp-doc-body"><pre>{d.body}</pre></div>}
                </div>
              ))}
            </>
          )}

          {activeTab !== "doc" && (
            <div className="atp-section">
              <h3>Response</h3>
              <textarea className="atp-response" value={response} readOnly placeholder="Response will appear here..." />
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default ApiTesterPage;
